import React from 'react';

import {
  LivestreamItemsFragment,
  useCreateLivestreamItemMutation,
  useDeleteLivestreamItemMutation,
  useLivestreamQuery,
  useUpdateLivestreamItemMutation,
} from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { useManageItem } from '../../../helpers/useManageItem';
import Layout from '../../FormComponents/Layout';
import { pagesQuery } from '../../LeftContainer/graphql';
import { PageItemProps } from '../BasicItem';
import Form, { InitialValues, initialValues, validationSchema } from './Form';

export const LivestreamItem: React.FC<PageItemProps> = ({ onSaveHandler }) => {
  const { pageId, itemId, isNew, close } = useManageItem();
  const [create, { loading: creating }] = useCreateLivestreamItemMutation();
  const [update, { loading: updating }] = useUpdateLivestreamItemMutation();
  const [destroy, { loading: destroying }] = useDeleteLivestreamItemMutation();

  const { data, loading } = useLivestreamQuery({
    variables: {
      pageId,
      id: itemId!,
    },
    skip: isNew,
  });

  const item = data?.page?.livestreamItem;

  const deleteLivestream = async () => {
    if (itemId) {
      await destroy({
        variables: {
          id: itemId,
          pageId,
        },
        update(cache) {
          deletePageItem({ pageId, itemId, cache });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
  };

  const save = async (values: InitialValues): Promise<void> => {
    if (item?.id) {
      await update({
        variables: {
          attributes: {
            id: item.id,
            pageId: pageId,
            title: values.title || '',
            description: values.description || '',
            buttonText: values.buttonText || '',
            buttonUrl: values.buttonUrl || '',
          },
        },
        update: (_cache, { data }) => close(data?.updateLivestreamItem),
      });
    } else {
      await create({
        variables: {
          attributes: {
            pageId: pageId,
            title: values.title || '',
            description: values.description || '',
            buttonText: values.buttonText || '',
            buttonUrl: values.buttonUrl || '',
          },
        },
        update: (cache, { data }) => {
          addItemToPage<LivestreamItemsFragment | undefined>(cache, pageId, data?.createLivestreamItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
      if (onSaveHandler) {
        onSaveHandler();
      }
      close();
    }
  };

  return (
    <Layout
      headerText={`${isNew ? 'Add a' : 'Update'} Livestream Item`}
      validationSchema={validationSchema}
      initialValues={initialValues(item)}
      onSubmit={save}
      Component={Form}
      loading={loading}
      updating={updating || destroying || creating}
      onDelete={deleteLivestream}
      isNew={isNew}
      item={item}
      close={close}
      pageId={pageId}
    />
  );
};
